import type { CandidatureResume } from './dossiers.js';

/**
 * Recherche par mot-clé dans les postulations et les CV (demande du
 * 2026-09-19). Le texte des CV est celui déjà extrait à l'ingestion
 * (`documents.texte_extrait`, OCR compris) : rien n'est relu sur le disque, et
 * un CV dont le texte n'a pas pu être extrait ne se trouve pas par son contenu.
 */

/** En deçà, la recherche ne part pas : un mot d'une lettre ramènerait tout. */
export const RECHERCHE_MIN = 2;
/** Mots-clés retenus au plus ; au-delà, les suivants sont ignorés. */
export const MOTS_CLES_MAX = 6;
/** Résultats rendus au plus ; l'écran annonce alors une liste tronquée. */
export const RESULTATS_MAX = 100;
/** Extraits montrés par candidat. */
export const EXTRAITS_MAX = 3;

export const ORIGINES_EXTRAIT = ['profil', 'synthese', 'cv', 'lettre', 'annonce'] as const;
export type OrigineExtrait = (typeof ORIGINES_EXTRAIT)[number];

export const LIBELLES_ORIGINE_EXTRAIT: Record<OrigineExtrait, string> = {
  profil: 'Fiche du candidat',
  synthese: 'Synthèse automatique',
  cv: 'CV',
  lettre: 'Lettre de motivation',
  annonce: 'Annonce',
};

export interface ExtraitRecherche {
  origine: OrigineExtrait;
  /** Nom du fichier pour un CV ou une lettre ; sinon l'intitulé de la rubrique. */
  libelle: string;
  /** Morceau de texte autour du mot trouvé, coupé aux blancs. */
  texte: string;
}

export interface ResultatRecherche {
  candidature: CandidatureResume;
  extraits: ExtraitRecherche[];
}

export interface ReponseRecherche {
  /** Mots effectivement cherchés, après nettoyage. */
  motsCles: string[];
  resultats: ResultatRecherche[];
  /** Vrai si `RESULTATS_MAX` a été atteint : affiner la recherche. */
  tronquee: boolean;
}

/**
 * Repli d'un caractère : sans accent et en minuscule, **à longueur
 * inchangée**, pour que la position trouvée dans le texte replié désigne le
 * même endroit dans le texte d'origine (l'extrait est montré tel qu'il a été
 * écrit). Un caractère dont la minuscule change de longueur reste tel quel.
 */
function plier(caractere: string): string {
  const sansAccent = caractere.normalize('NFD').replace(/\p{M}/gu, '') || caractere;
  const minuscule = sansAccent.toLowerCase();
  if (minuscule.length === caractere.length) return minuscule;
  const simple = caractere.toLowerCase();
  return simple.length === caractere.length ? simple : caractere;
}

/** Texte ramené en minuscules sans accents, à longueur identique. */
export function plierTexte(texte: string): string {
  return [...texte].map(plier).join('');
}

/**
 * Mots-clés d'une saisie : découpés aux blancs, repliés, dédoublonnés, les
 * trop courts écartés. Une expression entre guillemets reste d'un bloc.
 */
export function motsClesDe(saisie: string): string[] {
  const morceaux = saisie.match(/"[^"]*"|\S+/g) ?? [];
  const mots: string[] = [];
  for (const morceau of morceaux) {
    const mot = plierTexte(morceau.replace(/^"|"$/g, '').trim());
    if (mot.length < RECHERCHE_MIN || mots.includes(mot)) continue;
    mots.push(mot);
    if (mots.length >= MOTS_CLES_MAX) break;
  }
  return mots;
}

/**
 * Morceau de `texte` autour de la première occurrence de `mot`, coupé aux
 * blancs et encadré de « … » s'il ne commence ou ne finit pas le texte.
 * Rend `null` si le mot ne s'y trouve pas.
 */
export function extraitAutour(texte: string, mot: string, rayon = 70): string | null {
  const plie = plierTexte(texte);
  const cherche = plierTexte(mot);
  if (!cherche) return null;
  const position = plie.indexOf(cherche);
  if (position < 0) return null;

  let debut = Math.max(0, position - rayon);
  let fin = Math.min(texte.length, position + cherche.length + rayon);
  // Ne pas couper un mot en deux quand il reste de la place avant ou après.
  if (debut > 0) {
    const blanc = texte.slice(debut, position).search(/\s\S/);
    if (blanc >= 0) debut += blanc + 1;
  }
  if (fin < texte.length) {
    const reste = texte.slice(position + cherche.length, fin);
    const dernier = reste.search(/\s\S*$/);
    if (dernier >= 0) fin = position + cherche.length + dernier;
  }
  const morceau = texte.slice(debut, fin).replace(/\s+/g, ' ').trim();
  if (!morceau) return null;
  return `${debut > 0 ? '…' : ''}${morceau}${fin < texte.length ? '…' : ''}`;
}
